import {SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {ActionList, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {createContext, use, useEffect, useId, useMemo, useRef, useState} from 'react'

import styles from './CollapsibleSidebar.module.css'

type CollapsibleSidebarContextValue = {
  isExpanded: boolean
  /** Stable DOM id of the sidebar wrapper, used by Toggle for aria-controls. */
  sidebarId?: string
}

const CollapsibleSidebarContext = createContext<CollapsibleSidebarContextValue>({isExpanded: true})

/**
 * Read the current expanded/collapsed state of the enclosing
 * `<CollapsibleSidebar>`. Defaults to `isExpanded: true` if no provider is
 * present, so individual primitives render correctly when used outside of a
 * collapsible layout (e.g. in a non-collapsible `PageLayout.Pane`).
 */
export function useCollapsibleSidebarContext(): CollapsibleSidebarContextValue {
  return use(CollapsibleSidebarContext)
}

export interface CollapsibleSidebarProps {
  children: ReactNode
  isExpanded: boolean
  /**
   * Optional aria-label. When provided, the wrapper exposes a `complementary`
   * landmark to assistive technologies. Omit when the children already provide
   * their own landmark to avoid nesting landmarks.
   */
  ariaLabel?: string
  className?: string
  /**
   * The width of the sidebar when expanded. Mirrors PageLayout.Pane width values.
   */
  expandedWidth?: 'small' | 'medium' | 'large'
  /**
   * Content pinned to the bottom of the sidebar. Stays visible via
   * `position: sticky; bottom: 0` as the page scrolls. Typically
   * contains a `<CollapsibleSidebar.Toggle />` and/or a feedback link.
   */
  footer?: ReactNode
  'data-testid'?: string
}

const expandedWidthClass = {
  small: styles.expandedSmall,
  medium: styles.expandedMedium,
  large: styles.expandedLarge,
} as const

/**
 * A sidebar with a fixed icon-only condensed mode and an expanded mode.
 *
 * Unlike `PageLayout.Pane` with `resizable`, this component switches between
 * two discrete widths and does not provide a drag handle. Pair it with
 * `useCollapsibleSidebarState` for persisted expanded/collapsed state, and
 * render a `<CollapsibleSidebar.Toggle />` inside `children` to let users
 * change modes.
 *
 * Children rendered inside this component can call
 * `useCollapsibleSidebarContext()` (or use the `Item` primitive below) to
 * adapt their rendering to the current expanded/collapsed state without the
 * consumer having to thread the state through props.
 *
 * The wrapper is presentational by default — pass `ariaLabel` only when the
 * children do not already provide their own landmark.
 */
function CollapsibleSidebarRoot({
  children,
  isExpanded,
  ariaLabel,
  className,
  expandedWidth = 'small',
  footer,
  'data-testid': dataTestId,
}: CollapsibleSidebarProps) {
  const Wrapper = ariaLabel ? 'aside' : 'div'
  const sidebarId = useId()
  const contextValue = useMemo(() => ({isExpanded, sidebarId}), [isExpanded, sidebarId])
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isClipping, setIsClipping] = useState(false)

  const hasFooter = footer != null
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasFooter) return

    const observer = new IntersectionObserver(entries => setIsClipping(!entries[0]?.isIntersecting), {threshold: 0})
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasFooter])

  return (
    <CollapsibleSidebarContext value={contextValue}>
      <Wrapper
        id={sidebarId}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        data-expanded={isExpanded}
        className={clsx(styles.sidebar, isExpanded ? expandedWidthClass[expandedWidth] : styles.condensed, className)}
      >
        {children}
        {footer != null ? (
          <>
            <div ref={sentinelRef} className={styles.sentinel} />
            <div className={clsx(styles.footer, isClipping && styles.footerClipping)}>{footer}</div>
          </>
        ) : null}
      </Wrapper>
    </CollapsibleSidebarContext>
  )
}

export interface CollapsibleSidebarToggleProps {
  isExpanded: boolean
  onToggle: () => void
  /**
   * Override the default labels (Collapse sidebar / Expand sidebar).
   */
  collapseLabel?: string
  expandLabel?: string
  className?: string
}

/**
 * A toggle button intended to live at the bottom of a `<CollapsibleSidebar>`.
 * Uses `ActionList.Item` so the sizing, color, hover/focus states match the
 * NavList items in the sidebar. In condensed mode, the text label is
 * visually hidden by the `.sidebarCondensed` CSS overrides on the consumer.
 */
function CollapsibleSidebarToggle({
  isExpanded,
  onToggle,
  collapseLabel = 'Collapse sidebar',
  expandLabel = 'Expand sidebar',
  className,
}: CollapsibleSidebarToggleProps) {
  // Delay the visible label/icon when collapsing so the old text clips away
  // naturally during the CSS width transition, then snaps to the new value
  // once the sidebar has settled. On expand the label updates immediately so
  // the correct text reveals as the width grows.
  const [visualIsExpanded, setVisualIsExpanded] = useState(isExpanded)

  useEffect(() => {
    if (isExpanded) {
      const id = requestAnimationFrame(() => setVisualIsExpanded(true))
      return () => cancelAnimationFrame(id)
    } else {
      // 200ms matches the CSS transition duration
      const id = setTimeout(() => setVisualIsExpanded(false), 200)
      return () => clearTimeout(id)
    }
  }, [isExpanded])

  const label = isExpanded ? collapseLabel : expandLabel
  const visualLabel = visualIsExpanded ? collapseLabel : expandLabel
  const Icon = visualIsExpanded ? SidebarExpandIcon : SidebarCollapseIcon
  const {sidebarId} = use(CollapsibleSidebarContext)

  return (
    <Tooltip text={label} direction="e" _privateDisableTooltip={isExpanded}>
      <ActionList className={clsx(styles.toggleList, className)}>
        <ActionList.Item onSelect={onToggle} aria-expanded={isExpanded} aria-controls={sidebarId}>
          <ActionList.LeadingVisual>
            <Icon />
          </ActionList.LeadingVisual>
          {visualLabel}
        </ActionList.Item>
      </ActionList>
    </Tooltip>
  )
}

// Item primitive removed — consumers render their own items and read
// `useCollapsibleSidebarContext()` to branch on expanded/condensed state.

export const CollapsibleSidebar = Object.assign(CollapsibleSidebarRoot, {
  Toggle: CollapsibleSidebarToggle,
})
